<template>
    <transition name="fade">
        <div class="modal is-active" v-show="visible">
            <div class="modal-background" @click="destroy"></div>
            <div class="modal-content">
                <div class="popup-dialog">

                    <div class="popup-title">
                        <h1 class="title is-1">
                            {{ isEditing ? $t('design_wallet.online.title_edit_element') : $t('design_wallet.online.title_add_element') }}
                        </h1>

                        <div class="popup-close" @click.prevent="destroy">
                            <i class="icon">close</i>
                        </div>
                    </div>

                    <g-form :form="form">
                        <div class="popup-body popup-rows">

                            <div class="columns">
                                <div class="column is-one-third">
                                    <label>{{ $t('design_wallet.online.type') }}</label>
                                </div>

                                <div class="column">
                                    <div class="field-body">
                                        <g-select name="type"
                                                  :label="$t('design_wallet.online.type')"
                                                  :options="[
                                                      { name: $t('design_wallet.online.type_text'), value: 'text' },
                                                      { name: $t('design_wallet.online.type_button'), value: 'button' },
                                                  ]"/>
                                    </div>
                                </div>
                            </div>

                            <div class="columns">
                                <div class="column is-one-third">
                                    <label>{{ $t('design_wallet.online.text') }}</label>
                                </div>

                                <div class="column">
                                    <div class="field-body">
                                        <g-select :options="locales" name="locale"/>
                                    </div>

                                    <div class="field-body mt-3" id="textarea-wrapper">
                                        <div v-for="(locale, index) in locales" :key="index"
                                             v-show="form.locale === locale.value">

                                            <g-input :name="`content[${locale.value}]`"
                                                     :label="$t('packages.form.title', { language: locale.name })"
                                                     :maxlength="form.type === 'text' ? 500 : 50"
                                                     minlength="5"
                                                     :rows="form.type === 'text' ? 3 : 1"
                                                     ref="inputTitle"
                                                     type="textarea"
                                                     :autosize="false"/>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="columns" v-if="form.type === 'button'">
                                <div class="column is-one-third">
                                    <label>{{ $t('design_wallet.online.link') }}</label>
                                </div>

                                <div class="column">
                                    <div class="field-body mt-3" id="textarea-wrapper">
                                        <g-input name="link" :label="$t('design_wallet.online.link')"/>
                                    </div>
                                </div>
                            </div>

                            <div class="columns">
                                <div class="column is-one-third">
                                    <label>{{ $t('design_wallet.online.published') }}</label>
                                </div>

                                <div class="column">
                                    <div class="field-body">
                                        <g-switch name="published"
                                                  class="mt-4"
                                                  :options="[
                                                      { name: $t('design_wallet.online.option_yes'), value: true },
                                                      { name: $t('design_wallet.online.option_no'), value: false }
                                                  ]"/>
                                    </div>
                                </div>
                            </div>

                        </div>

                        <div class="popup-footer">
                            <a @click.prevent="destroy" href="#" role="button">{{ $t('design_wallet.online.cancel') }}</a>
                            <g-button :disabled="disabled" @click.prevent="submit">{{ isEditing ? $t('design_wallet.online.save') : $t('design_wallet.online.add') }}</g-button>
                        </div>
                    </g-form>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>

import {mapGetters} from "vuex";

export default {
    data() {
        return {
            form: null,
        }
    },

    computed: {
        ...mapGetters({
            locales: "application/availableLocalesTranslated",
        }),

        disabled() {
            const hasContent = this.locales.some(locale => {
                const contentField = this.form[`content[${locale.value}]`];
                return contentField && contentField.trim().length > 0;
            });

            const hasType = this.form.type !== '';
            const hasRequiredLink = this.form.type !== 'button' || (this.form.link && this.form.link.trim().length > 0);

            return !hasContent || !hasType || !hasRequiredLink;
        },

        isEditing() {
            return this.component !== null;
        }
    },

    props: {
        component: {
            type: Object,
            required: false,
            default: null
        },
    },

    created() {
        let formAdditions = {}

        // Set up form fields for each locale
        this.locales.forEach(locale => {
            if (this.isEditing === true) {
                // If editing, use existing content
                formAdditions[`content[${locale.value}]`] = this.component.content[locale.value] ?? '';
            }

            if (this.isEditing === false) {
                // If creating new, use empty strings
                formAdditions[`content[${locale.value}]`] = '';
            }
        })

        const locale = this.locales.map(locale => locale.value).includes(this.$i18n.locale)
            ? this.$i18n.locale
            : 'nl'

        this.form = new Form({
            locale: locale,
            published: this.isEditing ? (this.component.published_at !== null) : true,
            type: this.isEditing ? this.component.type : 'text',
            link: this.isEditing ? (this.component.content.link ?? '') : '',
            ...formAdditions
        });
    },

    methods: {
        submit(e) {
            e.preventDefault()
            e.stopPropagation()

            // Build content object with locales
            let content = {};
            this.locales.forEach(locale => {
                content[locale.value] = this.form[`content[${locale.value}]`] ?? '';
            });

            // Add link to content if it's a button type
            if (this.form.type === 'button' && this.form.link) {
                content.link = this.form.link;
            }

            // Just emit the event with the component data, no API call
            const componentData = {
                id: this.isEditing ? this.component.id : Date.now(),
                type: this.form.type,
                published_at: this.form.published ? new Date().toISOString() : null,
                content: content
            }

            if (this.isEditing === true) {
                this.$emit('component-updated', {
                    component: componentData
                })

                this.destroy()
                return
            }

            this.$emit('component-created', {
                component: componentData
            })
            this.destroy()
        }
    }
}
</script>

<style scoped lang="scss">
#textarea-wrapper div {
    width: 100%;
}
</style>
